import { Form, Formik, FormikConfig } from 'formik';
import { object, string, StringSchema } from 'yup';
import { useSetRecoilState } from 'recoil';
import escapeRegExp from 'lodash/escapeRegExp';
import ELEMENT from 'common/element';
import Page from 'components/common/Page/Page';
import * as K from 'common/constants';
import { gql, gqlRequest } from 'common/util/gqlRequest';
import Input from 'components/common/Input/Input';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import PageHeader from 'components/common/PageHeader/PageHeader';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';

import './ChangePasswordPublicPage.scss';
import { validEmailFormat } from 'common/util/string';
import { useEffect, useState } from 'react';

const REQUIRED_FIELD_VALIDATION_MSG = 'Required field';

/**
 * Form displayed when the password reset `token` is available in the url params
 *  allows user to change password
 */
// #region ChangePasswordForm
type ChangePasswordFormValues = {
  email: string;
  password: string;
  confirmPassword: string;
};
type ChangePasswordFormProps = {
  onSubmit: FormikConfig<ChangePasswordFormValues>['onSubmit'];
  initialEmail: string;
  errorMessage: string;
};

export const changePasswordFormValidationSchema = object({
  email: string().email('Must be a valid email address').required(REQUIRED_FIELD_VALIDATION_MSG),
  password: string().min(6, 'Must be at least 6 characters').required(REQUIRED_FIELD_VALIDATION_MSG),
  confirmPassword: string().when('password', (password: string, baseSchema: StringSchema) => {
    const escapedPassword = escapeRegExp(password);
    return baseSchema
      .matches(new RegExp(`^${escapedPassword}$`), 'Passwords should match')
      .required(REQUIRED_FIELD_VALIDATION_MSG);
  }),
});

function ChangePasswordForm(props: ChangePasswordFormProps): JSX.Element {
  const { onSubmit, initialEmail = '', errorMessage } = props;

  return (
    <Formik
      validationSchema={changePasswordFormValidationSchema}
      validateOnMount={true}
      onSubmit={onSubmit}
      initialValues={{ email: initialEmail, password: '', confirmPassword: '' }}
    >
      {({ handleChange, handleBlur, values, touched, errors, isValid, isValidating, isSubmitting }): JSX.Element => (
        <Form data-test-id={ELEMENT.changePasswordPagePublicChangePasswordForm}>
          {errorMessage && <div className="ChangePasswordPublicPage__ErrorMessage">{errorMessage}</div>}
          <Input
            type="email"
            name="email"
            placeholder="Email Address"
            value={values.email}
            onChange={handleChange}
            isInvalid={touched.email && !!errors.email}
            errorMessage={errors.email}
            disabled
            testId={ELEMENT.changePasswordPagePublicEmailInput}
          />
          <Input
            type="password"
            name="password"
            placeholder="New Password"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.password}
            isInvalid={touched.password && !!errors.password}
            errorMessage={errors.password}
            testId={ELEMENT.changePasswordPagePublicNewPasswordInput}
          />
          <Input
            type="password"
            name="confirmPassword"
            placeholder="Confirm New Password"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.confirmPassword}
            isInvalid={touched.confirmPassword && !!errors.confirmPassword}
            errorMessage={errors.confirmPassword}
            testId={ELEMENT.changePasswordPagePublicConfirmNewPasswordInput}
          />
          <Button
            type={'submit'}
            variant={ButtonVariant.PRIMARY}
            disabled={isSubmitting || isValidating || !isValid}
            testId={ELEMENT.changePasswordPagePublicChangePasswordButton}
          >
            Change Password
          </Button>
        </Form>
      )}
    </Formik>
  );
}
// #endregion

/**
 * Form displayed when the password reset `token` is missing in the url params
 *  allows user to request a new password reset email
 */
// #region RequestPasswordResetEmailForm
const requestPasswordResetValidationSchema = object({
  email: string().email('Must be a valid email address').required(REQUIRED_FIELD_VALIDATION_MSG),
});

type PasswordResetEmailFormValues = {
  email: string;
};

type RequestPasswordResetEmailFormProps = {
  onSubmit: FormikConfig<PasswordResetEmailFormValues>['onSubmit'];
  initialEmail: string;
};

function RequestPasswordResetEmailForm(props: RequestPasswordResetEmailFormProps): JSX.Element {
  const { onSubmit, initialEmail = '' } = props;

  return (
    <Formik
      validationSchema={requestPasswordResetValidationSchema}
      validateOnMount={true}
      onSubmit={onSubmit}
      initialValues={{ email: initialEmail }}
    >
      {({
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        submitForm,
        isValid,
        isValidating,
        isSubmitting,
      }): JSX.Element => (
        <Form data-test-id={ELEMENT.changePasswordPagePublicRequestPasswordResetEmailForm}>
          <Input
            type="email"
            name="email"
            placeholder="Email Address"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={touched.email && !!errors.email}
            errorMessage={errors.email}
            testId={ELEMENT.changePasswordPagePublicEmailInput}
          />
          <div className="ChangePasswordPublicPage__Message">
            The password reset token is invalid. Try re-opening the password reset link or request a new email
          </div>
          <Button
            type={'submit'}
            variant={ButtonVariant.PRIMARY}
            onClick={submitForm}
            disabled={isSubmitting || isValidating || !isValid}
            testId={ELEMENT.changePasswordPagePublicRequestResetButton}
          >
            Request Password Reset Email
          </Button>
        </Form>
      )}
    </Formik>
  );
}
// #endregion

function getValidEmailFromParam(userEmailParam: string) {
  if (validEmailFormat(userEmailParam)) {
    return userEmailParam;
  }
  return '';
}

function ChangePasswordPublicPage(): JSX.Element {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token') || '';
  const user = urlParams.get('user') || '';
  const userEmail = getValidEmailFromParam(urlParams.get('userEmail') || '');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!userEmail) {
      setErrorMessage('Invalid email address, your password cannot be reset. Please try again.');
    }
  }, []);

  async function setNewPassword({ password }: ChangePasswordFormValues): Promise<void> {
    const CHANGE_ACCOUNT_PASSWORD = gql`
      mutation resetPassword($user: String!, $password: String!, $token: String!, $timezoneOffset: String) {
        resetPassword(userId: $user, password: $password, token: $token, timezoneOffset: $timezoneOffset)
      }
    `;

    try {
      await gqlRequest({
        query: CHANGE_ACCOUNT_PASSWORD,
        variables: {
          user,
          password,
          token,
          timezoneOffset: new Date().getTimezoneOffset(),
        },
      });

      setPageAlertConfig({
        variant: PageAlertVariant.SUCCESS,
        message: String('Password has been successfully reset.'),
        autoHide: false,
      });
      setTimeout(() => {
        window.location.href = K.ROUTE.LOG_IN;
      }, 500);
      // Redirect user to the login page on success
    } catch (err) {
      setPageAlertConfig({
        variant: PageAlertVariant.ERROR,
        message: String(err),
        autoHide: false,
      });
    }
  }

  async function requestPasswordReset({ email }: PasswordResetEmailFormValues): Promise<void> {
    const REQUEST_PASSWORD_RESET = gql`
      mutation requestPasswordReset($email: String!) {
        requestPasswordReset(email: $email)
      }
    `;

    try {
      await gqlRequest({
        mutation: REQUEST_PASSWORD_RESET,
        variables: { email },
      });

      setPageAlertConfig({
        variant: PageAlertVariant.SUCCESS,
        message: `Instructions for resetting your password have been sent to ${email}`,
        autoHide: false,
      });
    } catch (err) {
      setPageAlertConfig({
        variant: PageAlertVariant.ERROR,
        message: String(err),
        autoHide: false,
      });
    }
  }

  return (
    <>
      <Page docTitle="Change Password" testId={ELEMENT.changePasswordPagePublic}>
        <div className="ChangePasswordPublicPage__Container">
          <PageHeader title={token ? 'Change Password' : 'Reset Password'} />
          {token ? (
            <ChangePasswordForm onSubmit={setNewPassword} initialEmail={userEmail} errorMessage={errorMessage} />
          ) : (
            <RequestPasswordResetEmailForm onSubmit={requestPasswordReset} initialEmail={userEmail} />
          )}
        </div>
      </Page>
    </>
  );
}

export default ChangePasswordPublicPage;
